import BadRequestError from "./BadRequestError";
import ConflictError from "./ConflictError";
import ExcelApiError from "./ExcelApiError";
import ForbiddenError from "./ForbiddenError";
import NotFoundError from "./NotFoundError";
import ProxyError from "./ProxyError";
import UnauthorizedError from "./UnauthorizedError";
import UnknownError from "./UnknownError";
import ValidationError from "./ValidationError";

export const handleError = (error: unknown, metadata?: any): Error => {
  // If already a domain error, return as-is (don't wrap again)
  if (
    error instanceof BadRequestError ||
    error instanceof UnauthorizedError ||
    error instanceof ForbiddenError ||
    error instanceof NotFoundError ||
    error instanceof ConflictError ||
    error instanceof ValidationError ||
    error instanceof ProxyError ||
    error instanceof ExcelApiError ||
    error instanceof UnknownError
  ) {
    return error;
  }

  // Check if it's an Office.js API error (OfficeExtension.Error)
  if (error instanceof OfficeExtension.Error) {
    const errorMessage = error.debugInfo?.message || error.message;
    return new ExcelApiError(errorMessage, error.code, metadata, error);
  }

  // If it's an Error with a statusCode property, map to appropriate domain error
  if (error instanceof Error) {
    const statusCode = (error as any).statusCode || (error as any).status;

    // If no status code, return the original error (don't wrap)
    if (!statusCode) {
      return error;
    }

    switch (statusCode) {
      case 400:
        return new BadRequestError(error.message, metadata, error);
      case 401:
        return new UnauthorizedError(error.message, metadata, error);
      case 403:
        return new ForbiddenError(error.message, metadata, error);
      case 404:
        return new NotFoundError(error.message, metadata, error);
      case 409:
        return new ConflictError(error.message, metadata, error);
      case 422:
        return new ValidationError(error.message, metadata, error);
      case 502:
      case 503:
      case 504:
        return new ProxyError(error.message, metadata, error);
      default:
        // For any other status code, wrap in UnknownError
        return new UnknownError(error.message, statusCode, metadata, error);
    }
  }

  // Not an Error instance, create UnknownError
  return new UnknownError(String(error), undefined, metadata);
};

export const serializeErrorForWarning = (
  error: unknown,
  additionalContext?: Record<string, unknown>
): Record<string, unknown> => {
  const baseContext = {
    error: error instanceof Error ? error.message : String(error),
    stack: error instanceof Error ? error.stack : undefined,
    errorName: error instanceof Error ? error.name : undefined,
    ...additionalContext,
  };

  // Remove undefined values to keep logs clean
  return Object.fromEntries(Object.entries(baseContext).filter(([_, v]) => v !== undefined));
};
